import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, X, ExternalLink, ListChecks, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { loadNotifPrefs, isQuietHours } from "@/components/NotificationPrefs";

// ─────────────────────────────────────────────────────────────
// Popup global: alerta sempre que aparecer uma NOVA tarefa Kommo.
// - Faz polling /api/kommo/tasks a cada 15s
// - Compara com snapshot anterior (localStorage por utilizador)
// - 1ª carga: marca tudo como visto silenciosamente (zero popups)
// - Quando aparecem IDs novos -> mostra modal com X
// ─────────────────────────────────────────────────────────────

const POLL_MS = 15000;

const storageKey = (email) => `meo_seen_task_ids_${email || "anon"}`;

const formatDeadline = (ts) => {
    if (!ts) return "Sem prazo";
    const d = new Date(ts * 1000);
    const dia = d.toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit" });
    const hora = d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
    if (hora === "23:59") return dia;
    return `${dia} · ${hora}`;
};

export default function NewTaskAlert() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [novas, setNovas] = useState([]); // tarefas novas a mostrar no popup
    const seenRef = useRef(null); // Set de IDs já vistos
    const initRef = useRef(false);

    const handleCriarProposta = async (t) => {
        try {
            // Cria proposta rápida pré-preenchida com nome+telefone do lead Kommo
            const payload = {
                nome: t.cliente_nome || "",
                telefone: t.cliente_telefone || "",
                kommo_lead_id: t.lead_id ? String(t.lead_id) : null,
            };
            const r = await api.post("/propostas/quick", payload);
            const propostaId = r?.data?.id;
            // Dismiss this popup item and navigate to editor
            setNovas((prev) => prev.filter((p) => p.id !== t.id));
            if (propostaId) {
                navigate(`/propostas/nova?id=${propostaId}`);
            } else {
                navigate("/propostas");
            }
        } catch (_) {
            navigate("/propostas");
        }
    };

    useEffect(() => {
        if (!user) return;
        let cancelled = false;

        // Carrega snapshot anterior do localStorage
        try {
            const raw = localStorage.getItem(storageKey(user.email));
            const arr = raw ? JSON.parse(raw) : null;
            seenRef.current = new Set(Array.isArray(arr) ? arr : []);
        } catch (_) {
            seenRef.current = new Set();
        }

        const persist = () => {
            try {
                localStorage.setItem(
                    storageKey(user.email),
                    JSON.stringify(Array.from(seenRef.current).slice(-500)),
                );
            } catch (_) {}
        };

        const tick = async () => {
            try {
                const r = await api.get("/kommo/tasks");
                if (cancelled) return;
                const groups = r.data?.groups || [];
                const allTasks = [];
                groups.forEach((g) => (g.tasks || []).forEach((t) => allTasks.push({ ...t, category: g.key })));

                const seen = seenRef.current;

                // Primeira carga (snapshot vazio E ainda não inicializámos):
                // marca TUDO como visto, não dispara popup.
                if (!initRef.current && seen.size === 0) {
                    allTasks.forEach((t) => seen.add(String(t.id)));
                    persist();
                    initRef.current = true;
                    return;
                }
                initRef.current = true;

                const novasDetectadas = allTasks.filter(
                    (t) => !seen.has(String(t.id)),
                );

                if (novasDetectadas.length > 0) {
                    novasDetectadas.forEach((t) => seen.add(String(t.id)));
                    persist();

                    // Respeita modo silêncio noturno: marca como visto mas não dispara popup nem som
                    const prefs = loadNotifPrefs();
                    if (isQuietHours(prefs)) return;

                    // junta às já em fila (caso popup esteja aberto)
                    setNovas((prev) => {
                        const ids = new Set(prev.map((p) => String(p.id)));
                        const merged = [...prev];
                        novasDetectadas.forEach((t) => {
                            if (!ids.has(String(t.id))) merged.push(t);
                        });
                        return merged;
                    });
                    // Som suave (best-effort) — só se prefs.sound
                    if (prefs.sound) {
                        try {
                            const ctx = new (window.AudioContext || window.webkitAudioContext)();
                            const o = ctx.createOscillator();
                            const g = ctx.createGain();
                            o.frequency.value = 880;
                            g.gain.value = 0.05;
                            o.connect(g);
                            g.connect(ctx.destination);
                            o.start();
                            o.stop(ctx.currentTime + 0.18);
                        } catch (_) {}
                    }
                }
            } catch (_) {
                // silencioso (suporte pode não ter Kommo, etc)
            }
        };

        tick();
        const id = setInterval(tick, POLL_MS);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [user]);

    const close = () => setNovas([]);

    if (!user || novas.length === 0) return null;

    return (
        <div
            data-testid="new-task-alert-popup"
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            onClick={close}
        >
            <div
                className="bg-white rounded-lg w-full max-w-md shadow-2xl overflow-hidden animate-[pop_180ms_ease-out]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="bg-gradient-to-r from-[#002FE6] to-[#1E3FFF] text-white px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Bell size={18} className="animate-pulse" />
                        <div>
                            <div className="text-[10px] uppercase tracking-wider opacity-90">
                                Alerta
                            </div>
                            <div className="font-bold text-sm">
                                {novas.length === 1
                                    ? "Nova tarefa criada"
                                    : `${novas.length} novas tarefas criadas`}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={close}
                        data-testid="new-task-alert-close"
                        aria-label="Fechar"
                        className="p-1.5 rounded hover:bg-white/20 transition"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="max-h-[55vh] overflow-y-auto">
                    {novas.slice(0, 8).map((t) => (
                        <div
                            key={t.id}
                            className="px-5 py-3 border-b border-zinc-100 last:border-b-0"
                        >
                            <div className="flex items-baseline justify-between gap-2">
                                <div className="font-semibold text-sm truncate">
                                    {t.cliente_nome || "—"}
                                </div>
                                <div className="text-[11px] font-mono text-zinc-500 shrink-0">
                                    {formatDeadline(t.complete_till)}
                                </div>
                            </div>
                            {t.text && (
                                <div className="text-xs text-zinc-600 mt-1 line-clamp-2 whitespace-pre-wrap">
                                    {t.text}
                                </div>
                            )}
                            <div className="flex flex-wrap items-center gap-2 mt-1.5">
                                {t.category === "proposta" && (
                                    <button
                                        onClick={() => handleCriarProposta(t)}
                                        className="inline-flex items-center gap-1 text-[11px] font-bold text-white bg-violet-600 hover:bg-violet-700 px-2.5 py-1 rounded shadow"
                                        data-testid={`btn-criar-proposta-${t.id}`}
                                    >
                                        <FileText size={11} /> Criar e enviar proposta
                                    </button>
                                )}
                                {t.url_kommo && (
                                    <a
                                        href={t.url_kommo}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-[11px] text-[#002FE6] font-semibold hover:underline"
                                    >
                                        <ExternalLink size={11} /> Abrir no Kommo
                                    </a>
                                )}
                            </div>
                        </div>
                    ))}
                    {novas.length > 8 && (
                        <div className="px-5 py-2 text-xs text-zinc-500 bg-zinc-50">
                            + {novas.length - 8} outras…
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 px-5 py-3 bg-zinc-50 border-t border-zinc-200">
                    <button
                        type="button"
                        onClick={close}
                        data-testid="new-task-alert-dismiss"
                        className="px-4 py-2 text-xs font-semibold text-zinc-600 hover:text-zinc-900"
                    >
                        Fechar
                    </button>
                    <Link
                        to="/instalacoes"
                        onClick={close}
                        data-testid="new-task-alert-goto"
                        className="px-4 py-2 bg-[#002FE6] hover:bg-[#0024B5] text-white text-xs font-semibold rounded-md flex items-center gap-1.5 transition"
                    >
                        <ListChecks size={14} /> Ver Instalações
                    </Link>
                </div>
            </div>
            <style>{`
                @keyframes pop {
                    0% { transform: scale(.92); opacity: 0; }
                    100% { transform: scale(1); opacity: 1; }
                }
            `}</style>
        </div>
    );
}
